import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, Delete, PaletteOutlined } from '@mui/icons-material';
import { useAppDispatch, useAppSelector, useConfirmation } from '@/hooks';
import {
  activateWebsiteOrganization,
  deactivateWebsiteOrganization,
  deleteOrganizationWebsite,
  organizationById,
  updateOrganizationWebsite,
} from '../../slice';
import { Organization } from '@/types';
import { Card } from '@/components';
import WebsiteSettings from './newWebsite/components/WebsiteSettings';
import { MenuItem, Website } from '@/types/website';
import MetaTags from '@/components/MetaTags';
import { Trash2 } from 'lucide-react';
import useRoutes from '@/hooks/useRoutes';

const OrganizationWebsites = () => {
  const [navigate] = useRoutes();
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [selectedWebsite, setSelectedWebsite] = useState<Website>();

  const websites = useAppSelector(
    (state) => state.organizations[organizationId ?? '']?.websites ?? []
  );

  const org = useAppSelector(
    organizationById(organizationId ?? '')
  ) as unknown as Organization;

  const websiteId = useMemo(() => {
    const website = org?.websites?.find(({ id }) => org?.website === id);
    if (!website) return null;
    return website.id;
  }, [org?.website, org?.websites]);

  const [renderConfirmaDeactivateDialog, confirmDeactivateWebsite] =
    useConfirmation({
      title: ' Desactivar Website',
      message: ' ¿ Está seguro que desea desactivar su sitio web ?',
      action: 'Desactivar',
      newActionDispatcher: deactivateWebsiteOrganization,
    });

  const [renderConfirmDeleteDialog, confirmDeleteWebsite] = useConfirmation({
    title: 'Eliminar Website',
    message: ' ¿ Está seguro que desea eliminar su sitio web ?',
    newActionDispatcher: deleteOrganizationWebsite,
  });

  const handleCreateWebsite = () => {
    navigate('organizations.websites.create', {
      organizationId
    })
  };

  const handleActivateWebsite = (websiteId: string | undefined) => {
    if (org?.website !== websiteId) {
      dispatch(
        activateWebsiteOrganization({
          organizationId: organizationId,
          website: websiteId,
        })
      );
    }
  };

  const handleDeleteWebsite = (websiteId: string) => {
    confirmDeleteWebsite({
      organizationId: organizationId ?? '',
      websiteId,
    });
  };

  const handleOpenWebsiteDrawer = (website: Website) => {
    setSelectedWebsite(website);
  };

  const formik = useFormik({
    initialValues: {
      metaData: {
        title: '',
        description: '',
      },
      menu: [
        {
          label: '',
          url: '',
        },
      ],
    },
    onSubmit: async (values: Website['metaData']) => {
      if (!organizationId) return;
      const payload = {
        organizationId: organizationId,
        website: { ...values, id: org?.website },
      };
      await dispatch(updateOrganizationWebsite(payload));
    },
  });

  const handleAddMenu = () => {
    const draft = {
      ...formik.values,
      menu: [
        ...formik.values.menu,
        {
          label: '',
          url: '',
        },
      ],
    };

    formik.setValues(draft);
  };

  const handleDeleteMenu = (index: number) => {
    const draft = [...formik.values.menu];
    draft.splice(index, 1);
    formik.setValues({
      ...formik.values,
      menu: draft,
    });
  };

  useEffect(() => {
    const website = org?.websites?.find((w) => w.id === org?.website);

    const initialValues = {
      metaData: {
        title: website?.metaData?.title ?? '',
        description: website?.metaData?.description ?? '',
      },
      menu: [
        {
          label: 'Inicio',
          url: '/',
        },
      ],
    };

    if (website?.menu?.length) {
      initialValues.menu = website?.menu;
    }

    if (website) formik.setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.website, org?.websites]);

  return (
    <>
      <Stack spacing={3}>
        <Stack spacing={2} direction={'row'}>
          <Stack flex={1}>
            <Typography variant="h2">Sitio Web</Typography>
          </Stack>
          {org?.website ? (
            <Stack spacing={2} direction={'row'}>
              <Button
                variant="text"
                color="success"
                onClick={() =>
                  navigate('organizations.websites.read', {
                    organizationId,
                    websiteId: org?.website,
                  })
                }
              >
                Ver
              </Button>
              <Button
                onClick={() =>
                  confirmDeactivateWebsite({
                    organizationId,
                    websiteId,
                  })
                }
              >
                Desactivar
              </Button>
            </Stack>
          ) : null}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h3">Versiones</Typography>
          {org?.websites?.length ? (
            websites.map((w, index) => (
              <Card
                key={`w-${index}`}
                title={w.title}
                actions={[
                  {
                    children: org.website === w.id ? 'Usando' : 'Usar',
                    size: 'small',
                    variant: 'outlined',
                    onClick: () => handleActivateWebsite(w.id),
                    disabled: org.website === w.id,
                  },
                  {
                    children: <PaletteOutlined />,
                    size: 'small',
                    variant: 'outlined',
                    onClick: () => {
                      navigate('organizations.websites.edit', {
                        organizationId,
                        websiteId: w.id
                      })
                    }
                  },
                  {
                    children: <Edit />,
                    size: 'small',
                    variant: 'outlined',
                    onClick: () => handleOpenWebsiteDrawer(w),
                  },
                  {
                    children: <Delete />,
                    size: 'small',
                    variant: 'outlined',
                    onClick: () => handleDeleteWebsite(w.id),
                  },
                ]}
                style={{
                  border: org.website === w.id ? '3px solid #005FEE' : 'none',
                  borderRadius: '12px',
                }}
              />
            ))
          ) : (
            <Stack spacing={2} alignItems={'center'}>
              <Typography variant="h3">No tienes sitio web todavía</Typography>
              <Button onClick={handleCreateWebsite}>Crear</Button>
            </Stack>
          )}

          <Stack spacing={3}>
            <Typography variant="h3">Meta Tags</Typography>
            <MetaTags
              title={org?.websites?.metaData?.title ?? org?.title}
              description={org?.websites?.metaData?.description ?? org?.slogan}
              image={org?.websites?.metaData?.favicon ?? org?.logo}
            />

            <TextField
              label="Título"
              name="metaData.title"
              value={formik.values.metaData?.title}
              onChange={formik.handleChange}
              size="small"
            />
            <TextField
              label="Descripción"
              name="metaData.description"
              value={formik.values.metaData?.description}
              onChange={formik.handleChange}
              size="small"
            />
          </Stack>
        </Stack>

        <Stack spacing={3}>
          <Typography variant="h3">Navegación</Typography>
          {formik.values.menu?.map((m: MenuItem, index: number) => (
            <Stack
              key={`${m}-${index}`}
              direction="row"
              spacing={2}
              alignItems="center"
            >
              <TextField
                label="Título"
                name={`menu.${index}.label`}
                size="small"
                fullWidth
                value={m.label ?? ''}
                onChange={formik.handleChange}
              />
              <TextField
                label="URL"
                name={`menu.${index}.url`}
                size="small"
                fullWidth
                value={m.url ?? ''}
                onChange={formik.handleChange}
              />
              <IconButton>
                <Trash2 onClick={() => handleDeleteMenu(index)} />
              </IconButton>
            </Stack>
          ))}
          <Button variant="outlined" onClick={handleAddMenu}>
            Agregar
          </Button>
        </Stack>

        <Button onClick={formik.handleSubmit} type="submit">
          Actualizar
        </Button>

        <WebsiteSettings
          selectedWebsite={selectedWebsite}
          onClose={() => setSelectedWebsite(undefined)}
        />
      </Stack>
      {renderConfirmaDeactivateDialog()}
      {renderConfirmDeleteDialog()}
    </>
  );
};

export default OrganizationWebsites;
